// date.ts — Sprint 145 (ADR-140): the ONE date formatter for CondoManager.
// ----------------------------------------------------------------------------
// Deliberately the same shape and idiom as `utils/currency.ts` (Sprint 138 /
// ADR-133): one module, cached `Intl` formatters, tolerant of the shapes the
// backend actually ships, and guarded in CI so divergence cannot creep back.
//
// TWO BUGS, ONE ROOT
//
// R14-10 — every date-only field rendered ONE DAY EARLY. `2026-02-28` showed as
// `2/27/2026`, `2026-07-31` as `7/30/2026`, `2025-03-20` as `3/19/2025`. The
// cause is a genuinely surprising corner of the language: `new Date(str)` parses
// a **date-only** ISO string as **UTC midnight**, but parses a date-TIME string
// as local. So `new Date('2026-02-28').toLocaleDateString()` renders UTC
// midnight in Toronto (UTC−5/−4) — which is the previous evening. Values
// carrying a time component rendered correctly, which is exactly why this looked
// like a data problem rather than a formatting one.
//
//   new Date('2026-02-28')           → 2026-02-27T19:00 local   ← WRONG
//   new Date('2026-02-28T00:00:00')  → 2026-02-28T00:00 local   ← right
//
// This module parses `YYYY-MM-DD` by hand into a LOCAL calendar date, so a
// calendar date means the day it says. Backend-side, Sprint 145 also guarantees
// these fields stay zone-less on the wire (`JacksonTimeConfig` gives an offset
// to `LocalDateTime` and pointedly does NOT touch `LocalDate`) — attaching a
// zone to a calendar date is the same mistake from the other end.
//
// R14-10 (second half) — the locale. Currency was correctly `en-CA` since
// Sprint 138, but dates called bare `toLocaleDateString()`, which follows the
// browser and produced US `M/D/YYYY` in a Canadian product.
//
// WHY THE DEFAULT IS `Feb 28, 2026` AND NOT `2026-02-28` OR `28/02/2026`
//
// The real harm in a numeric date is ambiguity: `03/04/2026` is two different
// days depending on who is reading it, and this product's dates carry legal and
// financial weight (Condominium Act s.76 status certificates, fee due dates,
// ownership effective dates). A spelled month cannot be misread. `en-CA` with a
// short month name is both unambiguous and locale-correct; `formatDateNumeric`
// is available for the compact ISO-style form where column width demands it.
//
// Use `formatDate` everywhere a server date is rendered. A CI guard
// (`.github/workflows/ci.yml`, "Date guard") fails the build on the UTC
// day-stamp idiom — the write-side twin of this bug (R14-9), where
// `new Date()` is converted to a UTC ISO string and truncated to its first ten
// characters — so the pair cannot come back. (The literal pattern is described
// in prose rather than written out, so the guard's own grep stays clean —
// the same trick `utils/currency.ts` uses for the wrong currency code.)

/** Matches a bare calendar date: the shape that `new Date()` gets wrong. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `Feb 28, 2026` — the default. A spelled month cannot be misread. */
const mediumDate = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

/** `2026-02-28` — ISO-style, for dense table columns. Still en-CA, still exact. */
const numericDate = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** `Feb 28, 2026, 3:04 p.m.` — for the moment family (offset-bearing values). */
const mediumDateTime = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export type DateInput = string | number | Date | null | undefined;

/**
 * Coerce a backend value into a `Date` **without** the UTC-midnight trap.
 *
 * A bare `YYYY-MM-DD` is constructed as local midnight on that calendar day, so
 * it renders as the day it names in every zone. Everything else (offset-bearing
 * RFC 3339, offset-less date-time, epoch millis, an existing `Date`) is handed
 * to the platform parser, which handles those shapes correctly.
 *
 * Exported so tests can assert the parse independently of the formatting.
 */
export function parseDateValue(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const fromEpoch = new Date(value);
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }

  const text = String(value).trim();
  const dateOnly = DATE_ONLY.exec(text);
  if (dateOnly) {
    // Local midnight, deliberately — see the header.
    const parsed = new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
    );
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Render a date as `Feb 28, 2026` (en-CA).
 *
 * Absent or unparseable input renders as the `fallback` (an em dash by default)
 * rather than `Invalid Date` — the same posture `currency()` takes with `NaN`.
 * A surface that a manager is about to act on must never show a broken value.
 */
export function formatDate(value: DateInput, fallback = '—'): string {
  const parsed = parseDateValue(value);
  return parsed ? mediumDate.format(parsed) : fallback;
}

/** Render a date as `2026-02-28` (en-CA), for dense columns. */
export function formatDateNumeric(value: DateInput, fallback = '—'): string {
  const parsed = parseDateValue(value);
  return parsed ? numericDate.format(parsed) : fallback;
}

/**
 * Render a moment as `Feb 28, 2026, 3:04 p.m.` (en-CA).
 *
 * Use only for the `LocalDateTime`/`Instant` family, which carries an offset on
 * the wire since Sprint 145. Passing a calendar date here would invent a
 * midnight that the data never asserted.
 */
export function formatDateTime(value: DateInput, fallback = '—'): string {
  const parsed = parseDateValue(value);
  return parsed ? mediumDateTime.format(parsed) : fallback;
}
